// Navigation state and constants
const NAV_CLASSES = {
  MEGA_OPEN: 'mega-nav--open',
  DROPDOWN_ACTIVE: 'navigation__dropdown--active',
  ACTIVE: 'active',
  ACTIVE_PAGE: 'active-page',
} as const;

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Normalizes a URL path by removing trailing slashes (except root)
 */
function normalizePath(raw: string): string {
  try {
    const url = new URL(raw, location.origin);
    const path = url.pathname;
    return path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path;
  } catch {
    const path = raw.split('?')[0].split('#')[0];
    return path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path || '/';
  }
}

export function initNavigation(): void {
  const megaNavContainer = document.querySelector<HTMLElement>('[data-component="mega-navigation-container"]');
  if (!megaNavContainer) return;

  // Store active tab state per dropdown (megaId)
  const tabStateMap = new Map<string, { tabIndex: number }>();
  const dropdownMap = new Map<string, HTMLElement>();
  let lastFocusedElement: HTMLElement | null = null;

  /**
   * Initializes tab behavior for a specific dropdown
   */
  function initTabListeners(megaId: string): void {
    const dropdown = megaNavContainer.querySelector<HTMLElement>(`[data-mega-id="${megaId}"]`);
    if (!dropdown) return;

    const tabList = dropdown.querySelector<HTMLElement>('[role="tablist"]');
    if (!tabList) return;

    const tabs = Array.from(tabList.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
    if (tabs.length === 0) return;

    const tabPanels = Array.from(dropdown.querySelectorAll<HTMLElement>('[role="tabpanel"]'));

    function showTab(targetTab: HTMLButtonElement): void {
      const activeIndex = tabs.indexOf(targetTab);
      if (activeIndex !== -1) {
        tabStateMap.set(megaId, { tabIndex: activeIndex });
      }

      tabs.forEach(tab => {
        const isSelected = tab === targetTab;
        tab.setAttribute('aria-selected', String(isSelected));
        tab.tabIndex = isSelected ? 0 : -1;
      });

      const targetPanelId = targetTab.getAttribute('aria-controls');
      tabPanels.forEach(panel => {
        panel.hidden = panel.id !== targetPanelId;
      });
    }

    // Keyboard navigation
    tabList.addEventListener('keydown', (e: KeyboardEvent) => {
      const currentTab = e.target as HTMLButtonElement;
      const currentIndex = tabs.indexOf(currentTab);
      if (currentIndex === -1) return;

      let newIndex: number | null = null;

      switch (e.key) {
        case 'ArrowDown':
          newIndex = (currentIndex + 1) % tabs.length;
          break;
        case 'ArrowUp':
          newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          newIndex = 0;
          break;
        case 'End':
          newIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      e.preventDefault();
      e.stopPropagation();
      showTab(tabs[newIndex]);
      tabs[newIndex].focus();
    });

    tabs.forEach(tab => {
      tab.addEventListener('click', e => {
        e.preventDefault();
        showTab(tab);
      });
    });
  }

  /**
   * Marks the current page as active in navigation
   */
  function setActivePage(): void {
    const currentPath = normalizePath(location.pathname);
    const links = Array.from(document.querySelectorAll<HTMLAnchorElement>('a.navigation__link[href]'));

    links.forEach(link => {
      const href = link.getAttribute('href') || '';
      if (href.startsWith('#')) {
        link.parentElement?.classList.remove(NAV_CLASSES.ACTIVE_PAGE);
        return;
      }

      const linkPath = normalizePath(href);
      link.parentElement?.classList.toggle(NAV_CLASSES.ACTIVE_PAGE, linkPath === currentPath);
    });

    // Set active-page on dropdown triggers if any link inside matches
    const dropdowns = Array.from(document.querySelectorAll<HTMLElement>('[data-js="dropdown"]'));
    dropdowns.forEach(dropdown => {
      let triggerId = dropdown.getAttribute('aria-labelledby') || dropdown.dataset.megaId;
      if (!triggerId) return;

      triggerId = triggerId.replace(/^["']|["']$/g, '');

      const triggers = Array.from(document.querySelectorAll<HTMLElement>('[data-mega-id]'));
      const trigger = triggers.find(el => {
        const id = el.dataset.megaId?.replace(/^["']|["']$/g, '');
        return id === triggerId;
      });

      if (!trigger) return;

      const dropdownLinks = Array.from(
        dropdown.querySelectorAll<HTMLAnchorElement>('a.navigation__link[href], a.navigation__dropdown-link[href]')
      );
      const hasActive = dropdownLinks.some(link => normalizePath(link.getAttribute('href') || '') === currentPath);

      trigger.classList.toggle(NAV_CLASSES.ACTIVE_PAGE, hasActive);
    });
  }

  /**
   * Updates CSS custom properties for header and overlay heights
   */
  function updateOverlayHeight(): void {
    const headerBlock = document.querySelector<HTMLElement>('.header-block');
    if (!headerBlock) return;

    const headerHeight = headerBlock.offsetHeight;
    const headerBottom = headerBlock.offsetTop + headerHeight;
    const bodyHeight = document.body.scrollHeight;
    const viewportHeight = window.innerHeight;

    const overlayHeight = Math.max(bodyHeight - headerBottom, viewportHeight * 2);

    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    document.documentElement.style.setProperty('--overlay-height', `${overlayHeight}px`);
  }

  /**
   * Closes all open dropdowns
   */
  function closeAll(isOpeningAnother = false): void {
    const openId = megaNavContainer.dataset.openId;
    if (!openId) return;

    megaNavContainer.classList.remove(NAV_CLASSES.MEGA_OPEN);
    megaNavContainer.setAttribute('aria-hidden', 'true');
    megaNavContainer
      .querySelectorAll(`.${NAV_CLASSES.DROPDOWN_ACTIVE}`)
      .forEach(el => el.classList.remove(NAV_CLASSES.DROPDOWN_ACTIVE));

    document.querySelectorAll(`[data-mega-id].${NAV_CLASSES.ACTIVE}`).forEach(el => {
      el.classList.remove(NAV_CLASSES.ACTIVE);
      el.querySelector<HTMLElement>('[aria-expanded]')?.setAttribute('aria-expanded', 'false');
    });

    delete megaNavContainer.dataset.openId;
    document.body.style.overflow = '';

    if (lastFocusedElement && !isOpeningAnother) {
      lastFocusedElement.focus();
    }
    lastFocusedElement = null;
  }

  /**
   * Opens a specific dropdown by ID
   */
  function open(id: string, trigger: HTMLElement): void {
    if (megaNavContainer.dataset.openId === id) return;

    closeAll(true);
    updateOverlayHeight();

    const megaNavView = dropdownMap.get(id);
    if (!megaNavView) {
      console.warn(`Mega nav dropdown not found for id: ${id}`);
      return;
    }

    // Restore tab state if tabs exist
    const tabList = megaNavView.querySelector<HTMLElement>('[role="tablist"]');
    if (tabList) {
      const tabs = Array.from(tabList.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
      const tabPanels = Array.from(megaNavView.querySelectorAll<HTMLElement>('[role="tabpanel"]'));
      const savedState = tabStateMap.get(id);
      const activeIndex = savedState?.tabIndex ?? 0;
      const activeTab = tabs[activeIndex];

      if (activeTab) {
        tabs.forEach((tab, index) => {
          const isActive = index === activeIndex;
          tab.setAttribute('aria-selected', String(isActive));
          tab.tabIndex = isActive ? 0 : -1;

          if (isActive) {
            const panelId = tab.getAttribute('aria-controls');
            tabPanels.forEach(panel => {
              panel.hidden = panel.id !== panelId;
            });
          }
        });
      }
    }

    trigger.classList.add(NAV_CLASSES.ACTIVE);
    trigger.querySelector<HTMLElement>('[aria-expanded]')?.setAttribute('aria-expanded', 'true');
    megaNavView.classList.add(NAV_CLASSES.DROPDOWN_ACTIVE);
    megaNavContainer.classList.add(NAV_CLASSES.MEGA_OPEN);
    megaNavContainer.setAttribute('aria-hidden', 'false');
    megaNavContainer.dataset.openId = id;
    document.body.style.overflow = 'hidden';

    setTimeout(() => {
      lastFocusedElement = document.activeElement as HTMLElement;
    }, 0);
  }

  // Initialize
  megaNavContainer.classList.remove(NAV_CLASSES.MEGA_OPEN);
  megaNavContainer.setAttribute('aria-hidden', 'true');
  delete megaNavContainer.dataset.openId;

  // Move dropdowns to container and initialize tabs
  const dropdowns = Array.from(document.querySelectorAll<HTMLElement>('[data-js="dropdown"]'));
  dropdowns.forEach(dropdown => {
    let megaNavId = dropdown.getAttribute('aria-labelledby') || dropdown.dataset.megaId;
    if (!megaNavId) return;

    // Strip quotes if present
    megaNavId = megaNavId.replace(/^["']|["']$/g, '');

    dropdown.dataset.megaId = megaNavId;
    megaNavContainer.appendChild(dropdown);
    dropdownMap.set(megaNavId, dropdown);
    initTabListeners(megaNavId);
  });

  setActivePage();

  // Setup triggers
  const triggers = Array.from(document.querySelectorAll<HTMLElement>('[data-mega-id]:not([data-js="dropdown"])'));
  triggers.forEach(trigger => {
    let id = trigger.dataset.megaId;
    if (!id) return;

    // Strip quotes if present
    id = id.replace(/^["']|["']$/g, '');

    const link = trigger.matches('a') ? trigger : trigger.querySelector('a.navigation__link');
    const onClick = (ev: Event) => {
      ev.preventDefault();
      megaNavContainer.dataset.openId === id ? closeAll() : open(id, trigger);
    };

    (link || trigger).addEventListener('click', onClick);
  });

  // Close on outside click
  document.addEventListener('click', ev => {
    const target = ev.target as Node | null;
    if (!target || !megaNavContainer.dataset.openId) return;
    if (megaNavContainer.contains(target) || (target as Element).closest?.('[data-mega-id]')) return;
    closeAll();
  });

  // Keyboard handling: Escape and focus trapping
  document.addEventListener('keydown', ev => {
    if (ev.key === 'Escape' || ev.key === 'Esc') {
      closeAll();
      return;
    }

    const openId = megaNavContainer.dataset.openId;
    if (!openId || ev.key !== 'Tab') return;

    const currentDropdown = megaNavContainer.querySelector(`.${NAV_CLASSES.DROPDOWN_ACTIVE}`);
    if (!currentDropdown) return;

    const focusableElements = Array.from(currentDropdown.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      el => {
        if (el.hasAttribute('hidden') || el.getAttribute('aria-hidden') === 'true') return false;
        return !el.closest('[hidden], [aria-hidden="true"]');
      }
    );

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement as HTMLElement;

    // Tab into dropdown from trigger
    if (lastFocusedElement && activeElement === lastFocusedElement && !ev.shiftKey) {
      if (firstFocusable) {
        firstFocusable.focus();
        ev.preventDefault();
      }
      return;
    }

    // Focus trapping within dropdown
    if (ev.shiftKey && activeElement === firstFocusable) {
      lastFocusedElement?.focus();
      ev.preventDefault();
    } else if (!ev.shiftKey && activeElement === lastFocusable) {
      lastFocusedElement?.focus();
      ev.preventDefault();
    }
  });

  window.addEventListener('resize', updateOverlayHeight);
  updateOverlayHeight();
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNavigation);
} else {
  initNavigation();
}
